<template>
  <div class="pb-8">
    <template v-if="partnerUser.id">
      <div>
        <div class="text-center py-8">
          <v-avatar color="primary" size="75">
            <span class="text-white text-h5">{{ getInitials }}</span>
          </v-avatar>
          <h5 class="mt-3 font-weight-bold">
            {{ partnerUser.name }}
          </h5>
          <div class="mt-2 text-caption">
            {{ partnerUser.email }} | {{ partnerUser.phone }}
          </div>
        </div>
        <v-divider></v-divider>
      </div>
    </template>

    <div v-for="(menuGroup, idx) in menuGroups" :key="idx">
      <template v-if="menuGroup.display !== false">
        <h4 v-if="menuGroup.heading" class="pa-4 pb-0">
          {{ menuGroup.heading }}
        </h4>
        <template v-for="(item, itemIdx) in menuGroup.items" :key="itemIdx">
          <template v-if="item.display !== false">
            <RouterLink
              v-if="!item.modal"
              :id="item.id"
              :class="['more-page__row', 'gtm-link', item.class]"
              :to="item.route"
            >
              <span class="more-page__menu-icon">
                <icon :fill="primaryColor">{{ item.icon }}</icon>
              </span>
              <span class="more-page__row-title text-body-2 text-primary">
                {{ item.title }}
                <v-badge
                  v-if="item.notifs > 0"
                  class="ms-1"
                  :content="item.notifs"
                  inline
                ></v-badge>
              </span>
            </RouterLink>
            <button
              v-else
              :id="item.id"
              type="button"
              :class="['more-page__row', 'gtm-link', item.class]"
              @click="onMenuItemClick(item)"
            >
              <span class="more-page__menu-icon">
                <icon :fill="primaryColor">{{ item.icon }}</icon>
              </span>
              <span class="more-page__row-title text-body-2 text-primary">
                {{ item.title }}
                <v-badge
                  v-if="item.notifs > 0"
                  class="ms-1"
                  :content="item.notifs"
                  inline
                ></v-badge>
              </span>
            </button>
            <v-divider></v-divider>
          </template>
        </template>
        <div v-if="menuGroup.heading === 'Need Help?' && offer" class="more-page__need-help">
          <p class="ma-0 pb-5">
            <a
              class="ma-0 text-decoration-none text-primary"
              :href="`tel:${offer.support_phone}`"
            >
              {{ offer.support_phone }} (Call)
            </a>
          </p>
          <p class="ma-0">
            <a
              class="ma-0 text-decoration-none text-primary"
              :href="`sms:${offer.support_text}`"
            >
              {{ offer.support_text }} (Text)
            </a>
          </p>
          
        </div>
        <v-divider></v-divider>
      </template>
    </div>

    <div v-if="partnerUser.id" class="px-4 py-7">
      <v-btn
        id="more_signout_btn"
        class="rounded-pill gtm-click"
        color="primary"
        size="large"
        variant="outlined"
        width="100%"
        @click="logout"
      >
        Sign Out
      </v-btn>
    </div>

    <confirmation-modal
      v-model="modals.confirmation"
      @close-modal="modals.confirmation = false"
    ></confirmation-modal>

    <suggestion-modal
      v-model="modals.suggestion"
      @close-modal="modals.suggestion = false"
      @confirmation-modal="modals.confirmation = $event"
    ></suggestion-modal>
  </div>
</template>

<script>
import { RouterLink } from 'vue-router';
import { mapGetters, mapState } from 'vuex';
import Confirmation from './Shared/Modals/Confirmation.vue';
import Suggestion from './Shared/Modals/Suggestion.vue';
import {
  initializeIntercom,
  openIntercom,
  isIntercomAvailable
} from '@/utils/intercom';
import { useTheme } from 'vuetify';

export default {
  components: {
    ConfirmationModal: Confirmation,
    SuggestionModal: Suggestion,
    RouterLink
  },
  setup() {
    const theme = useTheme();

    return { theme };
  },
  data() {
    return {
      modals: {
        confirmation: false,
        suggestion: false,
        needHelp: false
      },
      loading: false
    };
  },
  computed: {
    ...mapGetters('deals', {
      hasGamifiedDealsWithOrders: 'hasGamifiedDealsWithOrders',
      getDeals: 'list'
    }),
    ...mapGetters('products', { standaloneProducts: 'standalone' }),
    ...mapState(['messages', 'offer', 'partner', 'partnerUser']),
    primaryColor() {
      return (
        this.offer?.styles?.colors?.primary?.main ||
        this.partner?.styles?.colors?.primary?.main ||
        this.theme?.global?.current?.value?.colors?.primary ||
        this.theme?.themes?.value?.light?.colors?.primary ||
        '#cccccc'
      );
    },
    deals() {
      return this.getDeals || [];
    },
    menuGroups() {
      return [
        {
          display: !!this.partnerUser.id,
          heading: '',
          items: [
            {
              icon: 'Messages',
              title: 'Messages',
              route: { name: 'Messages', query: { t: window.token } },
              notifs: this.messages?.unread || 0,
              id: 'more_messages_lnk'
            },
            {
              icon: 'UnorderedList',
              title: 'Manage Subscriptions',
              route: { name: 'Subscriptions', query: { t: window.token } },
              class: 'subscriptions--gtm'
            },
            {
              icon: 'Orders',
              title: 'Order History',
              route: { name: 'OrderHistory', query: { t: window.token } },
              id: 'more_order_history_lnk'
            }
          ]
        },
        ...((this.offer && this.offer.use_points) ||
        this.hasGamifiedDealsWithOrders
          ? [
              {
                display: !!this.partnerUser.id,
                heading: 'Rewards',
                items: [
                  {
                    icon: 'Rewards',
                    title: 'Rewards History',
                    route: {
                      name: 'RewardsHistory',
                      query: { t: window.token }
                    },
                    id: 'more_rewards_history_lnk'
                  }
                ]
              }
            ]
          : []),
        {
          display: true,
          heading: 'Need Help?',
          items: [
            {
              display: !!this.partnerUser.id && isIntercomAvailable(),
              icon: 'Faqs',
              title: 'Request Support',
              modal: true,
              function: this.openIntercom,
              class: 'd-flex align-center',
              id: 'more_request_support_lnk'
            },
            {
              icon: 'Faqs',
              title: 'Common Questions',
              route: { name: 'CommonQuestions', query: { t: window.token } },
              class: 'd-flex align-center',
              id: 'more_faqs_lnk'
            }
          ]
        },
        {
          display: !!this.partnerUser.id,
          heading: 'App Suggestions',
          items: [
            {
              icon: 'Improvements',
              title: 'Suggest Feature/Improvements',
              modal: true,
              modalName: 'suggestion',
              id: 'more_suggest_feature_lnk'
            }
          ]
        }
      ];
    },

    getInitials() {
      // eslint-disable-next-line camelcase
      const { first_name: firstName = '', last_name: lastName = '' } =
        this.partnerUser;
      return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    }
  },
  methods: {
    onMenuItemClick(item) {
      if (item.function) {
        item.function.call(this);
        return;
      }

      if (item.modal && item.modalName) {
        this.modals[item.modalName] = true;
      }
    },
    async logout() {
      try {
        await this.$api.auth.logout();
        const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
        window.location.href = `${base}/login?t=${window.token}`;
      } catch (error) {
        console.error('Could not logout', error);
      }
    },
    async openIntercom() {
      openIntercom();
    },
    initializeIntercom() {
      initializeIntercom({
        partnerUser: this.partnerUser,
        partner: this.partner,
        deals: this.deals,
        standaloneProducts: this.standaloneProducts
      });
    }
  },
  mounted() {
    this.initializeIntercom();
  }
};
</script>

<style lang="scss" scoped>
.success-modal {
  border-radius: 1rem;
}

:deep(.v-dialog--fullscreen) {
  box-shadow: none !important;
}

.v-radio {
  &:deep(.v-selection-control__input) {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    border: solid 2px rgb(var(--v-theme-primary));
    margin-right: 10px;

    & .v-selection-control__ripple {
      left: -1rem;
    }
  }

  &.v-selection-control--dirty {
    &:deep(.v-selection-control__input) {
      background: rgb(var(--v-theme-primary));
      box-shadow: 0 0 0 3px #ffffff inset;
    }
  }
}

.more-page__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  text-decoration: none;
  min-height: 48px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: rgb(var(--v-theme-primary));
  text-align: left;
}

.more-page__row:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.more-page__menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  flex-shrink: 0;
  padding: 16px 0 16px 16px;
  margin-right: 16px;
}

.more-page__row-title {
  flex: 1;
  padding: 16px 16px 16px 0;
  display: flex;
  align-items: center;
}

.more-page__need-help {
  padding: 24px 16px;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

:deep(.v-divider.v-theme--light) {
    // border-color: rgba(0, 0, 0, 0.12);
    border-width: thin 0 0 0;
    opacity: 1;
}
:deep(span.more-page__menu-icon) {
    margin-top: 16px !important;
    margin-bottom: 16px !important;
    min-width: 24px !important;
    max-width: 42.25px !important;
}

:deep(.v-btn--density-default) {
  height: 40px;
  font-size: 0.875rem;
  border-radius: 9999px !important;
}

</style>
